import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { SectionHeader } from "@/components/site/Features";
import { useDiscord } from "@/context/DiscordContext";
import KentuckyPin from "@/components/site/KentuckyPin";
import FactionChip from "@/components/site/FactionChip";

const STATUS_STYLES = {
  alive: { dot: "bg-[#4ADE80]", label: "Alive", text: "text-[#4ADE80]" },
  missing: { dot: "bg-[#C0A875]", label: "Missing", text: "text-[#C0A875]" },
  dead: { dot: "bg-[#C0392B]", label: "K.I.A.", text: "text-[#C0392B]" },
};

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [graveyard, setGraveyard] = useState([]);
  const [factions, setFactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const { isConnected, user } = useDiscord();

  useEffect(() => {
    Promise.all([
      api.get("/members").then((r) => r.data.members || []).catch(() => []),
      api.get("/graveyard").then((r) => r.data.graveyard || []).catch(() => []),
      api.get("/factions").then((r) => r.data.factions || []).catch(() => []),
    ])
      .then(([m, g, f]) => {
        setMembers(m);
        setGraveyard(g);
        setFactions(f);
      })
      .finally(() => setLoading(false));
  }, []);

  const factionsById = useMemo(() => {
    const map = {};
    for (const f of factions) map[f.id] = f;
    return map;
  }, [factions]);

  const filtered = useMemo(() => {
    if (!q.trim()) return members;
    const needle = q.toLowerCase();
    return members.filter(
      (m) =>
        (m.character_name || "").toLowerCase().includes(needle) ||
        (m.alias || "").toLowerCase().includes(needle) ||
        (m.discord_username || "").toLowerCase().includes(needle)
    );
  }, [members, q]);

  const filteredGraves = useMemo(() => {
    if (!q.trim()) return graveyard;
    const needle = q.toLowerCase();
    return graveyard.filter(
      (m) =>
        (m.character_name || "").toLowerCase().includes(needle) ||
        (m.alias || "").toLowerCase().includes(needle) ||
        (m.discord_username || "").toLowerCase().includes(needle)
    );
  }, [graveyard, q]);

  const opened =
    members.find((m) => m.discord_id === openId) ||
    graveyard.find((m) => m.id === openId) ||
    null;
  const openedIsGrave = !!(opened && opened.archived_at);
  const isMe = isConnected && user && opened && !openedIsGrave && opened.discord_id === user.id;

  return (
    <div className="App min-h-screen">
      <Navbar />
      <main className="pt-[72px]">
        <section className="py-24 sm:py-28 border-t border-[#283524]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <SectionHeader
                eyebrow="// SURVIVORS"
                title="Who's still standing."
                desc="Each name on this wall is a real person playing a real life out there. Click any survivor to read their story."
              />
              <div className="flex items-center gap-2 border border-[#283524] bg-[#161D14] px-3 py-2 max-w-sm w-full">
                <Search size={14} className="text-[#8D9A87]" />
                <input
                  data-testid="members-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or alias…"
                  className="bg-transparent text-sm text-[#E2E4DF] flex-1 outline-none font-mono-ui placeholder:text-[#8D9A87]/60"
                />
              </div>
            </div>

            <div className="mt-12">
              {loading ? (
                <div className="text-center py-16">
                  <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={28} />
                </div>
              ) : filtered.length === 0 && filteredGraves.length === 0 ? (
                <p className="text-[#8D9A87] font-mono-ui text-sm">
                  // No survivors found{q ? " matching your search." : "."}
                </p>
              ) : (
                <>
                  <MemberGroups members={filtered} onCardClick={(id) => setOpenId(id)} />
                  {filteredGraves.length > 0 && (
                    <GraveyardSection graves={filteredGraves} onCardClick={(id) => setOpenId(id)} />
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {opened && (
          <MemberDetailModal
            member={opened}
            isMe={isMe}
            isGrave={openedIsGrave}
            faction={opened.faction_id ? factionsById[opened.faction_id] : null}
            onClose={() => setOpenId(null)}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

function MemberGroups({ members, onCardClick }) {
  const groups = [
    { id: "alive", label: "ALIVE" },
    { id: "missing", label: "MISSING" },
  ];
  return (
    <div className="space-y-16">
      {groups.map((g) => {
        const items = members.filter((m) => (m.status || "alive") === g.id);
        if (items.length === 0) return null;
        const st = STATUS_STYLES[g.id];
        return (
          <div key={g.id} data-testid={`group-${g.id}`}>
            <div className="flex items-center gap-4 mb-6">
              <span className={`inline-flex items-center gap-2 label-tag ${st.text}`}>
                <span className={`w-2.5 h-2.5 ${st.dot} pulse-dot`} />
                {g.label}
              </span>
              <span className="font-mono-ui text-xs text-[#8D9A87]">
                {items.length} survivor{items.length === 1 ? "" : "s"}
              </span>
              <div className="flex-1 h-px bg-[#283524]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {items.map((m) => (
                <MemberCard
                  key={m.discord_id}
                  member={m}
                  onClick={() => onCardClick(m.discord_id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GraveyardSection({ graves, onCardClick }) {
  return (
    <div className="mt-16" data-testid="group-graveyard">
      <div className="flex items-center gap-4 mb-6">
        <span className="inline-flex items-center gap-2 label-tag text-[#C0392B]">
          <span className="w-2.5 h-2.5 bg-[#C0392B]" />
          GRAVEYARD — KILLED IN ACTION
        </span>
        <span className="font-mono-ui text-xs text-[#8D9A87]">
          {graves.length} fallen
        </span>
        <div className="flex-1 h-px bg-[#283524]" />
      </div>
      <p className="font-mono-ui text-[11px] text-[#8D9A87] mb-6 max-w-2xl leading-relaxed">
        // Remembered. These survivors didn't make it. Their stories stay on the wall.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {graves.map((g) => (
          <GraveCard key={g.id} grave={g} onClick={() => onCardClick(g.id)} />
        ))}
      </div>
    </div>
  );
}

function GraveCard({ grave, onClick }) {
  const charName = grave.character_name || "Unnamed";
  const died = grave.archived_at ? new Date(grave.archived_at).toLocaleDateString() : null;
  return (
    <button
      data-testid={`grave-card-${grave.id}`}
      onClick={onClick}
      className="text-left bg-[#0F1410] border border-[#1F2A1C] p-6 flex flex-col gap-4 hover:border-[#C0392B] card-lift opacity-90 hover:opacity-100 transition-opacity"
    >
      <div className="flex items-start justify-between">
        <span className="inline-flex items-center gap-2 label-tag text-[#C0392B]">
          <span className="w-2 h-2 bg-[#C0392B]" />
          K.I.A.
        </span>
        <span className="font-mono-ui text-[10px] text-[#8D9A87]">
          @{grave.discord_username || "—"}
        </span>
      </div>
      <div className="relative w-full aspect-square bg-[#0D110C] border border-[#1F2A1C] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 tactical-grid opacity-30" />
        {grave.avatar_data_url ? (
          <img
            src={grave.avatar_data_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover grayscale contrast-90"
          />
        ) : (
          <span className="relative font-display text-5xl text-[#5C6B51] uppercase">
            {charName[0]}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-display uppercase text-[#A8B5A0] text-xl tracking-tight">
          {charName}
        </h3>
        {grave.alias && (
          <p className="label-tag mt-1 text-[#8D9A87]">"{grave.alias}"</p>
        )}
        {died && (
          <p className="mt-2 font-mono-ui text-[10px] text-[#5C6B51]">
            // FELL {died}
          </p>
        )}
      </div>
      {grave.bio && (
        <p className="text-[#5C6B51] text-xs leading-relaxed line-clamp-3">{grave.bio}</p>
      )}
    </button>
  );
}

function MemberCard({ member, onClick }) {  const st = STATUS_STYLES[member.status] || STATUS_STYLES.alive;
  const charName = member.character_name || "Unnamed";
  return (
    <button
      data-testid={`member-card-${member.discord_id}`}
      onClick={onClick}
      className="text-left bg-[#161D14] border border-[#283524] p-6 flex flex-col gap-4 hover:border-[#C0A875] card-lift"
    >
      <div className="flex items-start justify-between">
        <span className={`inline-flex items-center gap-2 label-tag ${st.text}`}>
          <span className={`w-2 h-2 ${st.dot}`} />
          {st.label}
        </span>
        <span className="font-mono-ui text-[10px] text-[#8D9A87]">
          @{member.discord_username || "—"}
        </span>
      </div>
      <div className="relative w-full aspect-square bg-[#0D110C] border border-[#283524] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 tactical-grid opacity-50" />
        {member.avatar_data_url ? (
          <img src={member.avatar_data_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <span className="relative font-display text-5xl text-[#C0A875] uppercase">
            {charName[0]}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-display uppercase text-[#E2E4DF] text-xl tracking-tight">
          {charName}
        </h3>
        {member.alias && (
          <p className="label-tag mt-1 text-[#C0A875]">"{member.alias}"</p>
        )}
      </div>
      {member.bio && (
        <p className="text-[#8D9A87] text-xs leading-relaxed line-clamp-3">{member.bio}</p>
      )}
    </button>
  );
}

function MemberDetailModal({ member, onClose, isMe, isGrave, faction }) {
  const st = isGrave
    ? { dot: "bg-[#C0392B]", label: "K.I.A.", text: "text-[#C0392B]" }
    : STATUS_STYLES[member.status] || STATUS_STYLES.alive;
  const charName = member.character_name || "Unnamed";
  const fellOn = isGrave && member.archived_at
    ? new Date(member.archived_at).toLocaleDateString()
    : null;
  return (
    <div
      data-testid="member-detail-modal"
      className="fixed inset-0 z-50 bg-[#0D110C]/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={`relative bg-[#161D14] border ${isGrave ? "border-[#1F2A1C]" : "border-[#283524]"} max-w-3xl w-full p-8 sm:p-10`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="member-detail-close"
          className="absolute top-4 right-4 text-[#8D9A87] hover:text-[#E2E4DF] font-mono-ui text-xs uppercase tracking-widest"
        >
          [ESC] Close
        </button>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="relative w-32 h-32 sm:w-40 sm:h-40 shrink-0 bg-[#0D110C] border border-[#283524] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 tactical-grid opacity-50" />
            {member.avatar_data_url ? (
              <img
                src={member.avatar_data_url}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover ${isGrave ? "grayscale" : ""}`}
              />
            ) : (
              <span className="relative font-display text-6xl text-[#C0A875] uppercase">
                {charName[0]}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-2 label-tag ${st.text}`}>
              <span className={`w-2 h-2 ${st.dot}`} />
              {st.label}
            </span>
            <h2 className={`mt-2 font-display uppercase text-3xl sm:text-4xl tracking-tight ${isGrave ? "text-[#A8B5A0]" : "text-[#E2E4DF]"}`}>
              {charName}
            </h2>
            {member.alias && <p className="mt-1 text-[#C0A875]">"{member.alias}"</p>}
            <p className="mt-2 font-mono-ui text-xs text-[#8D9A87]">
              @{member.discord_username || "—"} ·{" "}
              {isGrave
                ? `Fell ${fellOn || "—"}`
                : `Approved ${member.approved_at ? new Date(member.approved_at).toLocaleDateString() : "—"}`}
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-[#283524] pt-6">
          <DetailCell label="Age" value={member.age ?? "—"} />
          <DetailCell label="Origin" value={member.origin || "—"} />
          <DetailCell label="Occupation" value={member.occupation || "—"} />
          <DetailCell label="Faction">
            {faction ? (
              <div className="flex items-center gap-2 flex-wrap">
                <FactionChip faction={faction} size="sm" />
                <span
                  className="font-display uppercase text-[#E2E4DF] text-sm tracking-tight"
                  data-testid="faction-full-name"
                >
                  {faction.name}
                </span>
              </div>
            ) : (
              <span className="font-display uppercase text-[#E2E4DF] text-base">
                {member.faction || "—"}
              </span>
            )}
          </DetailCell>
        </div>

        <div className="mt-6">
          <div className="label-tag mb-2">Bio</div>
          <p className="text-[#E2E4DF] text-sm leading-relaxed whitespace-pre-wrap">
            {member.bio || "// No bio on file yet."}
          </p>
        </div>

        {member.last_seen && (
          <p
            className="mt-6 font-mono-ui text-xs inline-flex items-center gap-2"
            data-testid="last-seen-line"
          >
            <KentuckyPin size={14} color="#9CC279" />
            <span className="text-[#8D9A87] tracking-[0.18em]">// LAST SEEN:</span>
            <span className="text-[#9CC279] font-semibold">{member.last_seen}</span>
          </p>
        )}

        {isGrave && (
          <p className="mt-6 font-mono-ui text-[11px] text-[#5C6B51] italic">
            // This survivor is locked in the archive. Their story stays on the wall.
          </p>
        )}

        {isMe && (
          <div className="mt-8 pt-6 border-t border-[#283524] text-right">
            <Link
              to="/members/me"
              data-testid="member-edit-link"
              className="inline-block border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em]"
            >
              Edit your profile →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailCell({ label, value, children }) {
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      {children ? children : (
        <div className="font-mono-ui text-sm text-[#E2E4DF]">{value}</div>
      )}
    </div>
  );
}
